/**
 * PieceProfileModal — PIECE GUARD Step 1 (spec §4).
 *
 * Created ONCE PER ITEM, EVER. The first time a `pcs` item is saved without a profile,
 * saving is blocked until this screen is completed — and for most items that is now a
 * SINGLE TAP.
 *
 * THE FIRST QUESTION IS THE ONLY ONE EVERY ITEM ANSWERS: pieces, or packets?
 *
 *   "In pieces"  → there is nothing left to ask. A piece is a piece; the profile is
 *                  saved as pack = 1 on the spot and the bill goes straight through.
 *                  No read-back, no arithmetic, no warnings afterwards (the guard's
 *                  pack and price ladders are switched off for single-piece items —
 *                  see pieceGuard.js §9.6). Nagging somebody about "1 coconut" is how
 *                  you teach a team to ignore every warning you will ever show them.
 *
 *   "In packets" → THEN the pack size is asked, and THEN the read-back does the
 *                  arithmetic out loud:
 *                      "→ 16 pcs, ₹2.81 each"   vs   "→ 4 pcs, ₹11.25 each"
 *                  because a wrong entry produces an ABSURD SENTENCE that any human
 *                  recognises with zero training. Everything the packet path costs is
 *                  spent only on the items that actually arrive in packets.
 *
 * The old §9.8 "is this actually sold by weight?" screen is gone. It fired on a CATEGORY
 * guess (dairy/meat/vegetables) before the person had said anything at all, so the very
 * first thing a vegetable item showed was an accusation about a mistake nobody had made
 * yet — an extra screen in front of every single item in the commonest categories, to ask
 * a question the unit dropdown on the row already answers. The unit is theirs to set; this
 * screen's job is to find out what one piece is.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';

/**
 * S1 — mine the pack count from the bill's own text, HERE as well as on the server.
 *
 * The backend's `minePackFromName` is fed `it.item_name`, which for a matched row is the
 * DATABASE name with the "4N / X4 / PKT OF 4" notation already stripped — and rows that
 * still carry the raw OCR text have no item_id, so Layer C skipped them entirely. The
 * pre-fill therefore never fired in either case, and "confirm rather than type" was
 * "type" every time. This screen has the raw names in its hands (display_name /
 * original_ocr_name), so it mines them itself.
 *
 * Deliberately conservative and identical in spirit to the server's version: a wrong
 * pre-fill is worse than none, because it turns a question a human would have answered
 * into a default they will silently accept. 1 is not evidence of a pack — it is the
 * absence of one.
 */
export const minePackFromNames = (...names) => {
  const patterns = [
    /\bPKT\s*OF\s*(\d{1,3})\b/,
    /\bPACK\s*OF\s*(\d{1,3})\b/,
    /\b(\d{1,3})\s*(?:NOS|NO'?S)\b/,
    /\b(\d{1,3})\s*PCS?\b/,
    /\b(\d{1,3})\s*'?S\b/,
    /\b(\d{1,3})\s*N\b/,
    /\b(\d{1,3})\s*U\b/,
    /\bX\s*(\d{1,3})\b/,
    /\b(\d{1,3})\s*X\b/,
    /\b\d{1,3}\s*[*]\s*(\d{1,3})\b/,
    /\((\d{1,3})\)/,
  ];
  for (const name of names) {
    const s = String(name || '').toUpperCase();
    if (!s) continue;
    for (const re of patterns) {
      const m = s.match(re);
      if (!m) continue;
      const n = parseInt(m[1], 10);
      if (Number.isInteger(n) && n >= 2 && n <= 500) return n;
    }
  }
  return null;
};

const PieceProfileModal = ({
  isOpen,
  itemId,
  itemName,
  // The line that triggered this screen, used for the read-back arithmetic.
  enteredQuantity,
  enteredTotal,
  // Pack notation mined from the bill's own text by the backend (§4). An independent,
  // correct default, so most people confirm rather than type.
  suggestedPiecesPerPack = null,
  // S1 — the RAW names, before the matcher stripped the pack notation off them. The
  // backend can only mine the DB name for a matched row, which no longer contains
  // "4N"/"X4"/"PKT OF 4"; these still do.
  rawItemName = null,
  originalOcrName = null,
  onSave,          // async ({ piece_description, pieces_per_pack, arrival_mode, ... }) => void
  onCancel,
  saving = false,
  error = null,
}) => {
  // S1 — the server's answer wins where it has one; otherwise mine the raw names here.
  const minedPack = suggestedPiecesPerPack || minePackFromNames(rawItemName, originalOcrName, itemName);

  /**
   * THE ONE QUESTION EVERY ITEM ANSWERS. null until the person taps.
   *
   *   'pieces'  → sold one by one. pack = 1, saved immediately, nothing else asked.
   *   'packets' → the pack size and the read-back follow.
   *
   * Neither is pre-selected. A default here is the assumption wearing a disguise, and
   * this single answer decides whether the item is ever checked against a pack size
   * again for the rest of its life.
   */
  const [arrivalChoice, setArrivalChoice] = useState(null);
  const [piecesPerPack, setPiecesPerPack] = useState(minedPack || '');

  const description = `one ${String(itemName || 'piece').trim()}`;

  /**
   * P3 — WHAT THE NUMBER ON THE ROW MEANT. null until the person says.
   *
   *   'packets' → the N they typed was packets, so the row becomes N × pack pieces
   *   'pieces'  → the N they typed was already pieces, so the row is left alone
   *
   * There used to be no second option. The read-back ALWAYS assumed packets and multiplied,
   * so somebody who had correctly entered 12 pcs of a 4-pack item was told "This row will be
   * saved as 48 pcs" with NO honest way out — and the only escape was to lie and declare the
   * item "single pieces", which permanently corrupts the very profile the guard depends on.
   * That is the spec's explicitly forbidden failure — creating stock that does not
   * physically exist — built into the screen designed to prevent it.
   */
  const [quantityMeaning, setQuantityMeaning] = useState(null);

  /**
   * Clear the screen when it OPENS FOR A DIFFERENT ITEM — and at no other time.
   *
   * This used to also depend on values derived from props and recomputed on every render.
   * Any re-render of the parent that changed one — and the parent re-renders plenty, not
   * least when the debounced piece-profile refetch lands a few hundred milliseconds after a
   * save — re-ran this effect and WIPED whatever the person had typed so far.
   *
   * Identity is `isOpen` + `itemId`. Nothing else is identity; everything else is detail.
   * The mined pack is read through a ref so seeding the field does not make the seed a
   * reason to erase the form.
   */
  const minedPackRef = useRef(minedPack);
  minedPackRef.current = minedPack;
  useEffect(() => {
    if (!isOpen) return;
    setArrivalChoice(null);
    setPiecesPerPack(minedPackRef.current || '');
    setQuantityMeaning(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemId]);

  const pack = parseInt(piecesPerPack, 10);

  // Any change to the numbers invalidates a choice made about the OLD numbers. Without
  // this, someone could pick "the 1 I entered was packets" against a pack of 4, then edit
  // the pack to 12 and save the stale choice — which is exactly the guard being bypassed.
  useEffect(() => { setQuantityMeaning(null); }, [piecesPerPack]);

  /**
   * BOTH readings of the number, priced. This is the read-back's actual power: the
   * arithmetic is spoken out loud for each possibility side by side, so the absurd one is
   * VISIBLY absurd rather than being the only one offered.
   *
   *     "→ 48 pcs, ₹2.29 each"   vs   "→ 12 pcs, ₹9.17 each"
   */
  const options = useMemo(() => {
    const qty = Number(enteredQuantity);
    const total = Number(enteredTotal);
    const hasPrice = Number.isFinite(total) && total > 0;
    if (!Number.isFinite(qty) || qty <= 0) return null;
    if (!Number.isInteger(pack) || pack < 1) return null;
    return {
      qty,
      total: hasPrice ? total : null,
      hasPrice,
      packets: { pieces: qty * pack, perPiece: hasPrice ? total / (qty * pack) : null },
      pieces: { pieces: qty, perPiece: hasPrice ? total / qty : null },
    };
  }, [enteredQuantity, enteredTotal, pack]);

  const needsMeaningChoice = !!options && pack > 1;

  /**
   * S3 — SAVING WITHOUT A READ-BACK, when there is nothing to read back.
   *
   * The onboarding door passes enteredTotal = 0, so `options` has no price and the
   * arithmetic cannot be spoken. The piece is defined now and the arithmetic is confirmed
   * at the first priced purchase (which is precisely what the money check is). A screen
   * whose whole purpose is to be completed must never become a dead end with Cancel as the
   * only exit.
   *
   * The floor is 2 here, not 1: this branch is only ever reached after somebody tapped
   * "in packets", and a "packet" of 1 is the single-piece answer wearing the wrong label.
   */
  const canSave =
    Number.isInteger(pack) && pack >= 2 &&
    (!needsMeaningChoice || quantityMeaning !== null) &&
    !saving;

  if (!isOpen) return null;

  // ── THE ONLY QUESTION EVERY ITEM MEETS ──────────────────────────────────────
  // One tap for a pieces item, which is most of them. The packet path — pack size,
  // read-back, and every pack/price check the guard runs afterwards — is entered only
  // by the people whose items actually arrive in packets.
  /**
   * `!== 'packets'` rather than `=== null`, and the difference is a real one.
   *
   * Tapping "In pieces" fires the save and sets the choice, but the modal is not unmounted
   * by that — the parent unmounts it when the save SUCCEEDS. On `=== null` the screen
   * therefore flipped straight to the packet questions the instant somebody said the item
   * had no packets, and a save that then failed left them staring at a pack-size box
   * asking a question they had just answered the other way. Staying here keeps the two
   * buttons, the "Saving…" line and any error in one place.
   */
  if (arrivalChoice !== 'packets') {
    return (
    <div className="piece-profile-overlay" style={overlayStyle}>
        <div style={modalStyle}>
          <button type="button" style={xBtn} onClick={onCancel} aria-label="Close">×</button>
          <h3 style={h3Style}>How does {itemName} arrive?</h3>
          <p style={pStyle}>
            You only have to answer this once for this item.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
            <button
              type="button"
              style={choiceBtn}
              disabled={saving}
              onClick={() => {
                setArrivalChoice('pieces');
                onSave({
                  item_id: itemId,
                  piece_description: description.trim(),
                  pieces_per_pack: 1,
                  arrival_mode: 'single',
                  piece_weight_g: null,
                  readback_confirmed: true,
                  quantity_meaning: 'pieces',
                });
              }}
            >
              <span style={choiceTitle}>In pieces</span>
              <span style={choiceSub}>Counted one by one — 1 = 1 {itemName}</span>
            </button>
            <button
              type="button"
              style={choiceBtn}
              disabled={saving}
              onClick={() => setArrivalChoice('packets')}
            >
              <span style={choiceTitle}>In packets</span>
              <span style={choiceSub}>Several pieces in one packet</span>
            </button>
          </div>
          {saving && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 14 }}>Saving…</div>}
          {error && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 12 }}>{error}</div>}
        </div>
      </div>
    );
  }

  // ── THE PACKET PATH — pack size, then the read-back ──────────────────────────
  return (
    <div className="piece-profile-overlay" style={overlayStyle}>
      <div style={modalStyle}>
        <button type="button" style={xBtn} onClick={onCancel} aria-label="Close">×</button>
        <h3 style={h3Style}>How many pieces in one packet of {itemName}?</h3>

        {/* ── THE ONE QUESTION ───────────────────────────────────────────────── */}
        <div style={{ marginTop: 14 }}>
          <label style={labelStyle}>Pieces in one packet</label>
          <input
            type="number"
            autoFocus
            min="2"
            step="1"
            value={piecesPerPack}
            onChange={(e) => setPiecesPerPack(e.target.value)}
            style={{ ...inputStyle, width: 75, textAlign: 'center' }}
          />
          {minedPack && Number(piecesPerPack) === Number(minedPack) && (
            <div style={{ fontSize: 12, color: '#047857', marginTop: 4 }}>
              Read from the bill: "{minedPack} per packet". Change it if that is wrong.
            </div>
          )}
        </div>

        {/* ── Q3 — THE READ-BACK, AS A QUESTION WITH TWO HONEST ANSWERS ─────── */}
        {needsMeaningChoice && (
          <div style={readbackStyle}>
            <style>{`
              .meaning-row-wrap {
                display: flex;
                align-items: center;
                gap: 10px;
                font-size: 13px;
                cursor: pointer;
                margin-top: 10px;
                padding: 10px 12px;
                border-radius: 8px;
                box-sizing: border-box;
                border: 1px solid #e5e7eb;
                background: #fff;
                text-align: left;
              }
              .meaning-option-content {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                align-items: center;
                gap: 6px;
                font-size: 13px;
                line-height: 1.4;
                text-align: left;
              }
              .meaning-option-sub {
                font-size: 12.5px;
                font-weight: 600;
                color: #4b5563;
                text-align: left;
              }
              @media (max-width: 480px) {
                .meaning-row-wrap {
                  align-items: flex-start;
                }
                .meaning-option-content {
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 2px;
                }
              }
            `}</style>
            <div style={{ fontSize: 15, lineHeight: 1.5, marginTop: 14 }}>
              You entered <strong>{options.qty}</strong> on the row
              {options.hasPrice && <> for <strong>₹{options.total.toFixed(2)}</strong></>}.
              {' '}Was that <strong>{options.qty} packet{options.qty === 1 ? '' : 's'}</strong>,
              or <strong>{options.qty} loose piece{options.qty === 1 ? '' : 's'}</strong>?
            </div>

            <label
              className="meaning-row-wrap"
              style={quantityMeaning === 'packets' ? meaningRowOn : {}}
            >
              <input
                type="radio"
                name="piece-quantity-meaning"
                aria-label={`The ${options.qty} I entered was packets`}
                checked={quantityMeaning === 'packets'}
                onChange={() => setQuantityMeaning('packets')}
                style={{ accentColor: '#111827', cursor: 'pointer', flexShrink: 0 }}
              />
              <div className="meaning-option-content">
                <div>
                  The <strong>{options.qty}</strong> I entered was <strong>packet{options.qty === 1 ? '' : 's'}</strong>
                </div>
                <div className="meaning-option-sub" data-testid="meaning-result-packets">
                  ({options.packets.pieces} pcs{options.hasPrice && <>, ₹{options.packets.perPiece.toFixed(2)} each</>})
                </div>
              </div>
            </label>

            <label
              className="meaning-row-wrap"
              style={quantityMeaning === 'pieces' ? meaningRowOn : {}}
            >
              <input
                type="radio"
                name="piece-quantity-meaning"
                aria-label={`The ${options.qty} I entered was already pieces`}
                checked={quantityMeaning === 'pieces'}
                onChange={() => setQuantityMeaning('pieces')}
                style={{ accentColor: '#111827', cursor: 'pointer', flexShrink: 0 }}
              />
              <div className="meaning-option-content">
                <div>
                  The <strong>{options.qty}</strong> I entered was already <strong>piece{options.qty === 1 ? '' : 's'}</strong>
                </div>
                <div className="meaning-option-sub" data-testid="meaning-result-pieces">
                  ({options.pieces.pieces} pcs{options.hasPrice && <>, ₹{options.pieces.perPiece.toFixed(2)} each</>})
                </div>
              </div>
            </label>

          </div>
        )}

        {error && (
          <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 12 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button type="button" style={equalSecondaryBtn} onClick={() => setArrivalChoice(null)}>
            Back
          </button>
          <button
            type="button"
            disabled={!canSave}
            style={{ ...equalPrimaryBtn, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}
            onClick={() => onSave({
              item_id: itemId,
              piece_description: description.trim(),
              pieces_per_pack: pack,
              arrival_mode: 'packet',
              piece_weight_g: null,
              readback_confirmed: true,
              ...(quantityMeaning === 'packets' && options
                ? { corrected_quantity: options.packets.pieces }
                : {}),
              quantity_meaning: needsMeaningChoice ? quantityMeaning : 'pieces',
            })}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── styles kept inline so this drops in without a CSS build step ──────────────
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16,
};
const modalStyle = {
  position: 'relative',
  background: '#fff', borderRadius: 16, padding: '24px 18px 20px', width: '100%',
  maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const xBtn = {
  position: 'absolute', top: 12, right: 12,
  background: 'none', border: 'none', fontSize: 22, lineHeight: 1,
  color: '#000000', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontWeight: 700, zIndex: 2,
};
const h3Style = { margin: '0 0 8px', fontSize: 19, fontWeight: 700, color: '#111827', paddingRight: 30 };
const pStyle = { margin: '0 0 18px', fontSize: 14, color: '#4b5563', lineHeight: 1.5, paddingRight: 20 };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle = {
  width: '100%', padding: '9px 11px', border: '1px solid #d1d5db',
  borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};
const choiceBtn = {
  flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left',
  padding: '14px 16px', background: '#fff', color: '#111827',
  border: '1px solid #cbd5e1', borderRadius: 10, cursor: 'pointer',
};
const choiceTitle = { fontSize: 16, fontWeight: 700 };
const choiceSub = { fontSize: 13, color: '#6b7280' };
const meaningRow = {
  display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, cursor: 'pointer',
  marginTop: 10, padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
  border: '1px solid #e5e7eb', background: '#fff', textAlign: 'left',
};
const meaningRowOn = { borderColor: '#d1d5db', background: '#f9fafb' };
const meaningResult = { display: 'block', margin: 0, padding: 0, fontSize: 12.5, fontWeight: 600, color: '#4b5563', textAlign: 'left' };
const readbackStyle = {
  marginTop: 12, padding: 0,
};
const primaryBtn = {
  flex: 1,
  padding: '12px 18px', minHeight: 46, background: '#111827', color: '#fff', border: 'none',
  borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'center',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const equalPrimaryBtn = {
  flex: 1,
  padding: '12px 18px', minHeight: 46, background: '#111827', color: '#fff', border: 'none',
  borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'center',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const secondaryBtn = {
  flex: 1,
  padding: '12px 18px', minHeight: 46, background: '#fff', color: '#374151',
  border: '1px solid #d1d5db', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'center',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const equalSecondaryBtn = {
  flex: 1,
  padding: '12px 18px', minHeight: 46, background: '#fff', color: '#374151',
  border: '1px solid #d1d5db', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'center',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const linkBtn = {
  marginTop: 14, background: 'none', border: 'none', color: '#6b7280',
  fontSize: 13, textDecoration: 'underline', cursor: 'pointer', padding: 0,
};
const inlineLinkBtn = {
  background: 'none', border: 'none', color: '#2563eb', font: 'inherit',
  textDecoration: 'underline', cursor: 'pointer', padding: 0,
};

export default PieceProfileModal;
